import { useState } from 'react';

export interface ConflictRow {
  mediaLabel: string;
}

interface Props {
  conflicts: ConflictRow[];
  onCancel: () => void;
  onConfirm: (overwriteLabels: string[]) => void;
}

export default function ConflictModal({ conflicts, onCancel, onConfirm }: Props) {
  const [overwrites, setOverwrites] = useState<Set<string>>(new Set());

  const toggle = (label: string) => {
    setOverwrites((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const allSelected = overwrites.size === conflicts.length;

  const toggleAll = () => {
    setOverwrites(allSelected ? new Set() : new Set(conflicts.map((c) => c.mediaLabel)));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg w-full max-w-lg shadow-lg flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-sm">Duplicate Media Labels found</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {conflicts.length} tape{conflicts.length === 1 ? '' : 's'} in your file already
            exist{conflicts.length === 1 ? 's' : ''}. Tick the ones to overwrite — the rest will be skipped.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="w-4 h-4 cursor-pointer"
            />
            Select all (overwrite)
          </label>
          <div className="flex flex-col gap-1">
            {conflicts.map((c) => (
              <label
                key={c.mediaLabel}
                className="flex items-center gap-2 px-3 py-2 rounded bg-slate-50 hover:bg-slate-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={overwrites.has(c.mediaLabel)}
                  onChange={() => toggle(c.mediaLabel)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-xs font-mono text-slate-700 truncate">{c.mediaLabel}</span>
                <span
                  className={`ml-auto text-[11px] whitespace-nowrap ${
                    overwrites.has(c.mediaLabel) ? 'text-emerald-600 font-medium' : 'text-slate-400'
                  }`}
                >
                  {overwrites.has(c.mediaLabel) ? 'Overwrite' : 'Skip'}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md border border-slate-200 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(Array.from(overwrites))}
            className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap"
          >
            Import ({overwrites.size} overwrite, {conflicts.length - overwrites.size} skip)
          </button>
        </div>
      </div>
    </div>
  );
}